import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

export const THEMES = [
  'riministreet',
  'kamin',
  'klk',
  'niterra',
  'protoseguro',
  'servicenow',
  'silicon-labs',
  'tidewater',
  'vantris-energy',
] as const;

export type ThemeType = typeof THEMES[number];

export const isThemeType = (value: string | null | undefined): value is ThemeType =>
  !!value && (THEMES as readonly string[]).includes(value);

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private themeLinkId = 'theme-link';
  private themeLoadedSubject = new BehaviorSubject<boolean>(false);
  public themeLoaded$ = this.themeLoadedSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      const saved = localStorage.getItem('selectedTheme');
      this.setTheme(isThemeType(saved) ? saved : 'riministreet');
    }
  }

  setTheme(theme: ThemeType) {
    if (!isPlatformBrowser(this.platformId)) return;

    localStorage.setItem('selectedTheme', theme);

    const themePath = `themes/${theme}.css`;
    this.loadTheme(themePath);
  }

  private loadTheme(themePath: string) {
    if (!isPlatformBrowser(this.platformId)) return;

    let link = document.getElementById(this.themeLinkId) as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.id = this.themeLinkId;
      link.rel = 'stylesheet';
      link.type = 'text/css';
      document.head.appendChild(link);
    }

    link.onload = () => {
      const verify = () => {
        if (!isPlatformBrowser(this.platformId)) return;

        const blue = getComputedStyle(document.documentElement).getPropertyValue('--Chart-Blue');
        if (blue.trim()) {
          this.themeLoadedSubject.next(true);
        } else {
          requestAnimationFrame(verify);
        }
      };
      verify();
    };

    link.href = themePath;
  }

  getCurrentTheme(): ThemeType {
    if (isPlatformBrowser(this.platformId)) {
      const saved = localStorage.getItem('selectedTheme');
      return isThemeType(saved) ? saved : 'riministreet';
    }
    return 'riministreet';
  }
}
